import { useCallback, type ReactNode } from 'react';
import type { Permission } from '@nexops/shared';
import { useAuthStore } from './auth.store';

export type PermissionRule = Permission | readonly Permission[] | undefined;

/** True when `permissions` satisfies the rule. An array means "any of". No rule = allowed. */
export function checkPermission(permissions: readonly string[] | undefined, rule: PermissionRule): boolean {
  if (!rule) return true;
  if (!permissions) return false;
  const list: readonly Permission[] = typeof rule === 'string' ? [rule] : rule;
  return list.length === 0 || list.some((p) => permissions.includes(p));
}

/**
 * UI-level permission check. Hides UI only — the backend enforces access and data scope.
 * `usePermission('customers.read')`, `usePermission(['bi.board', 'bi.team'])` (any of).
 */
export function usePermission(rule: PermissionRule): boolean {
  return useAuthStore((s) => checkPermission(s.user?.permissions, rule));
}

/** Returns a checker bound to the current user — handy for filtering lists (nav items). */
export function usePermissionChecker(): (rule: PermissionRule) => boolean {
  const permissions = useAuthStore((s) => s.user?.permissions);
  return useCallback((rule: PermissionRule) => checkPermission(permissions, rule), [permissions]);
}

interface CanProps {
  permission: PermissionRule;
  children: ReactNode;
  fallback?: ReactNode;
}

export function Can({ permission, children, fallback = null }: CanProps) {
  return usePermission(permission) ? <>{children}</> : <>{fallback}</>;
}
